import { CSSProperties, useCallback } from "react";
import { defineMessages, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { IconCameraX, IconEyeSpark, IconHeart, IconHeartPin, IconLock } from "@tabler/icons-react";
import { AnyPeriod, classNames, toGQLPeriod } from "@ct-react/core";
import { LocaleLink, useLocaleFormatter } from "@ct-react/locale";
import { BookingOption } from "@ct-react/calendar";
import { buildArticleUrl } from "@shared/url";
import { AnyArticleDetails, isAnnualArticle, isRentalArticle, isSaleArticle, isSeasonalArticle } from "@shared/graphql";
import { card, common, pricing } from "../i18n/sharable-defs";
import { Ratio } from "../models/common";
import { SkeletonableProps } from "../tools/components";
import ImagesCarousel from "./medias/images-carousel";
import SpriteIcon from "./medias/sprite-icon";
import { useWishlist } from "../contexts/wishlist";

import "./article-card.scss";

type Props = AnyArticleDetails & {
  suggestion?: BookingOption;
  withInnerContextNav?: boolean;
};

const ArticleCard = (
  {
    loading,
    className,
    data
  }: SkeletonableProps<Props>) => {

  const intl = useIntl();
  const { print } = useLocaleFormatter();
  const { has, toggle } = useWishlist();

  const addToWishlist = (event: React.FormEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!data) return;
    const res = toggle(data);
  };

  const buildTarget = useCallback((data: AnyArticleDetails, period?: AnyPeriod) => {
    const url = buildArticleUrl(data);
    if (!isRentalArticle(data) || !period) return url;
    const formatted = toGQLPeriod(period);
    return `${url}&b_in=${formatted[0]}&b_out=${formatted[1]}`;
  }, []);

  const formatSuggestion = useCallback((opt: BookingOption) => {
    const period = print.period(opt.period, true, true);
    return opt.priceOnDemand
      ? `${intl.formatMessage(pricing.onDemand)} ${period}`
      : `${print.price(opt.price.amount, { trailingZeroDisplay: "stripIfInteger" })} ${period}`
  }, [ intl ]);

  const imageRatio: Ratio = "3/2";
  const wrapperClasses = classNames("article-card", className, {
    "no-image": !loading && data?.images.length == 0
  });

  if (loading || !data)
    return (
      <article className={wrapperClasses}>
        <Skeleton className="card-pictures" style={{ aspectRatio: imageRatio }} />
        <div className="card-resume">
          <Skeleton className="location" width="40%" />
          <Skeleton className="title" inline={true} />
          <Skeleton className="price" width="65%" />
        </div>
      </article>);

  const not_in_wishlist = !has(data.id);

  return (
    <article className={wrapperClasses}>
      <LocaleLink to={buildTarget(data, data.suggestion?.period)}
                  state={{...!!data.withInnerContextNav && { innerNav: true }}}
                  aria-label="Découvrez le bien">

        <button className={classNames("add-wishlist-btn glass-card",{ activ : !not_in_wishlist })}
        onClick={addToWishlist}
        aria-label="Ajouter aux favoris">
            <IconHeart strokeWidth={1}/>
        </button>

        <div className="card-pictures"
             style={{ "--ccv-img-ratio": imageRatio } as CSSProperties}>
          {data.images.length > 0
            ? <ImagesCarousel ratio={imageRatio}
                              data={data.images}
                              fallbackAlt={data.title.value} />
            : <IconCameraX className="x-icon" />
          }
          <ul className="card-badges">
            {data.novelty &&
              <li className="novelty">
                <IconEyeSpark className="b-icon" />
                {intl.formatMessage(card.novelty)}
              </li>
            }
            {data.recommended &&
              <li className="recommended">
                <IconHeartPin className="b-icon" />
                {intl.formatMessage(card.recommended)}
              </li>
            }
            {(isSaleArticle(data) && data.sold) &&
              <li className="sold">
                <IconLock className="b-icon" />
                {intl.formatMessage(card.sold)}
              </li>
            }
          </ul>
        </div>

        <div className="card-resume glass-card">

          {isRentalArticle(data) &&
            <span className="price">
              {!!data.suggestion
                ? formatSuggestion(data.suggestion)
                : (data.lowestPrice.onDemand
                  ? intl.formatMessage(pricing.onDemand)
                  : intl.formatMessage(pricing.lowest, {
                    type: data.lowestPrice.type,
                    price: print.price(data.lowestPrice.value, { trailingZeroDisplay: "stripIfInteger" })
                  }))
              }
            </span>
          }

          {isSeasonalArticle(data) &&
            <span className="price">
              {(!data.price || data.price.onDemand)
                ? intl.formatMessage(pricing.onDemand)
                : (data.price.isMonthly ?
                  intl.formatMessage(pricing.byMonth, {
                    price: print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" })
                  })
                  : print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" }))
              }
            </span>
          }

          {isAnnualArticle(data) &&
            <span className="price">
              {data.price.onDemand
                ? intl.formatMessage(pricing.onDemand)
                : intl.formatMessage(pricing.byMonth, {
                  price: print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" })
                })
              }
            </span>
          }

          {isSaleArticle(data) &&
            <span className="price">
              {data.price.onDemand
                ? intl.formatMessage(pricing.onDemand)
                : print.price(data.price.value, { trailingZeroDisplay: "stripIfInteger" })
              }
            </span>
          }
          <div className="features">
            {(isRentalArticle(data) || isSeasonalArticle(data)) &&
              <>
              {data!.features.maxGuests &&
                <span>
                  <SpriteIcon hash="features.person" width={13}/>
                  {data!.features.maxGuests}
                  <div>{intl.formatMessage(common.countedGuests, { count: data!.features.maxGuests })}</div>
                </span>
              }
              {data!.features.bedrooms &&
                <span>
                  <SpriteIcon hash="features.bedrooms" width={14}/>
                  {data!.features.bedrooms}
                  <div>{intl.formatMessage(common.countedBedrooms, { count: data!.features.bedrooms })}</div>
                </span>
              }
              {data!.address.city &&
                <span><SpriteIcon hash="contact.pin-location" width={14}/>
                {data!.address.city}
                </span>
              }
              </>
            }
            {(isSaleArticle(data) || isAnnualArticle(data)) &&
              <>
              {data!.features.livingSpace &&
                <span>
                  <SpriteIcon hash="features.surface"/>
                  {data!.features.livingSpace}	m&sup2;
                </span>
              }
              {data!.features.bedrooms &&
                <span>
                  <SpriteIcon hash="features.rooms"/>
                  {data!.features.bedrooms}
                  <div>{intl.formatMessage(common.countedRooms, { count: data!.features.bedrooms })}</div>
                </span>
              }
              {data!.address.city &&
                <span><SpriteIcon hash="contact.pin-location" width={14}/>
                {data!.address.city}
                </span>
              }
              </>
            }
          </div>

        </div>
      </LocaleLink>
    </article>);
}

export default ArticleCard;
